import isEqual from 'lodash/fp/isEqual';
import isNil from 'lodash/fp/isNil';
import omitBy from 'lodash/fp/omitBy';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import messages from 'dashboard/lib/constants/messages';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  TableKey,
  TableView,
  TableViewConfiguration,
  TableViewStatus,
} from 'dashboard/models/table-view';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import Action from 'lib/interfaces/Action';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { DeepPartialExhaustive } from 'lib/types';

export const MESSAGES = {
  CREATE_SUCCESS: 'View created.',
  CREATE_FAILURE: 'Failed to create view. Please try again.',
  DELETE_SUCCESS: 'Table view deleted.',
  DELETE_FAILURE: 'Failed to delete table view. Please try again.',
  DELETE_FAILURE_LAST_VIEW: 'Failed to delete table view. Please try again.',
  DUPLICATE_FAILURE: 'Unable to duplicate table view.',
  LOAD_FAILURE: 'Failed to load table views.',
  SAVE_SUCCESS: 'View updated.',
  SAVE_FAILURE: 'Failed to save table view. Please try again.',
  PERMISSION_FAILURE: "You don't have permission to create a new table view.",
};

interface UseTableViewsConfig<AF> {
  tableKey: TableKey;
  onSetTableView: (tableView: TableView<AF> | null) => void;
  setTableViews: (tableViews: TableView<AF>[]) => Action<string, TableView<AF>[]>;
  setCurrentTableView: (currentTableView: string) => Action<string, string>;
  selectTableViews: (state: RootState) => TableView<AF>[];
  selectCurrentTableView: (state: RootState) => TableView<AF> | undefined;
  filters: AF;
}

export type UseTableViewsState<AF extends object> = ReturnType<typeof useTableViews<AF>>;

const useTableViews = <AF extends object>({
  tableKey,
  onSetTableView,
  setTableViews,
  setCurrentTableView,
  selectTableViews,
  selectCurrentTableView,
  filters,
}: UseTableViewsConfig<AF>) => {
  const dispatch = useDispatch();
  const tableViews = useSelector(selectTableViews);
  const currentTableView = useSelector(selectCurrentTableView);
  const canViewTableViews = usePermission(Permission.TableViewView);
  const canWriteTableViews = usePermission(Permission.TableViewWrite);

  const { loading: loadingTableViews, callback: loadTableViews } = useApiLoader({
    loader: async () => {
      const tableViews = await SessionClientDashboard.listResource<TableView<AF>>(
        ApiEndpoint.TableView,
        {
          filter: {
            'table-key': tableKey,
            status: TableViewStatus.Active,
          },
        },
      );

      dispatch(setTableViews(tableViews.data));
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
      } else {
        dispatch(showMessage(MESSAGES.LOAD_FAILURE, MessageType.Failure));
      }
    },
    onNetworkError: () => {
      dispatch(showMessage(messages.NETWORK_ERROR, MessageType.Failure));
    },
  });

  const { error: createTableViewError, callback: handleCreateTableView } = useApiCallback(
    async (name: string, configuration: TableViewConfiguration<AF>) => {
      const responseTableView = await SessionClientDashboard.postResourceCreate<TableView<AF>>({
        attributes: {
          name,
          configuration: configuration as DeepPartialExhaustive<TableViewConfiguration<AF>>,
          'table-key': tableKey,
        },
        type: ApiModel.TableView,
      });
      dispatch(setTableViews([...tableViews, responseTableView.data]));
      dispatch(setCurrentTableView(responseTableView.data.id));
      dispatch(showMessage(MESSAGES.CREATE_SUCCESS, MessageType.Success));
      return { id: responseTableView.data.id };
    },
    [tableViews],
  );

  useApiErrorHandler({
    error: createTableViewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(MESSAGES.PERMISSION_FAILURE, MessageType.Failure));
      } else {
        dispatch(showMessage(MESSAGES.CREATE_FAILURE, MessageType.Failure, false));
      }
    },
    onNetworkError: () => dispatch(showMessage(messages.NETWORK_ERROR, MessageType.Failure, false)),
  });

  const handleDuplicateTableView = useCallback(
    async (id?: string) => {
      const tableView = id ? tableViews.find((view) => view.id === id) : currentTableView;

      if (tableView) {
        return await handleCreateTableView(
          tableView.attributes.name,
          tableView.attributes.configuration,
        );
      }

      dispatch(showMessage(MESSAGES.DUPLICATE_FAILURE, MessageType.Failure));
    },
    [currentTableView, handleCreateTableView, tableViews, dispatch],
  );

  const handleSetCurrentTableView = useCallback(
    (id: string | null) => {
      if (!id) {
        dispatch(setCurrentTableView(''));
        onSetTableView(null);
        return;
      }

      const tableView = tableViews.find((tableView) => tableView.id === id);

      if (tableView) {
        dispatch(setCurrentTableView(tableView.id));
        onSetTableView(tableView);
      }
    },
    [dispatch, setCurrentTableView, tableViews, onSetTableView],
  );

  const { error: saveTableViewError, callback: handleSaveTableView } = useApiCallback(
    async (id: string, name: string, config?: TableViewConfiguration<AF>) => {
      const tableView = tableViews.find((tableView) => tableView.id === id);
      const configuration = config || tableView?.attributes.configuration;
      const responseTableView = await SessionClientDashboard.updateResource<TableView<AF>>({
        attributes: {
          name,
          configuration: configuration as DeepPartialExhaustive<TableViewConfiguration<AF>>,
        },
        id,
        type: ApiModel.TableView,
      });
      const nextTableViews = [...tableViews];
      const tableViewToUpdateIndex = tableViews.findIndex((view: TableView<AF>) => view.id === id);
      nextTableViews.splice(tableViewToUpdateIndex, 1, responseTableView.data);
      dispatch(setTableViews(nextTableViews));

      dispatch(showMessage(MESSAGES.SAVE_SUCCESS, MessageType.Success));
    },
    [tableViews],
  );

  useApiErrorHandler({
    error: saveTableViewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
      } else {
        dispatch(showMessage(MESSAGES.SAVE_FAILURE, MessageType.Failure, false));
      }
    },
    onNetworkError: () => dispatch(showMessage(messages.NETWORK_ERROR, MessageType.Failure, false)),
  });

  const { error: handleArchiveTableViewError, callback: handleArchiveTableView } = useApiCallback(
    async (id: string) => {
      await SessionClientDashboard.deleteResource<TableView<AF>>(ApiEndpoint.TableView, id);
      dispatch(showMessage(MESSAGES.DELETE_SUCCESS, MessageType.Success));
      await loadTableViews();
    },
    [loadTableViews],
  );

  useApiErrorHandler({
    error: handleArchiveTableViewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
      } else if (error.status === 400) {
        dispatch(showMessage(MESSAGES.DELETE_FAILURE_LAST_VIEW, MessageType.Failure));
      } else {
        dispatch(showMessage(MESSAGES.DELETE_FAILURE, MessageType.Failure, false));
      }
    },
    onNetworkError: () =>
      dispatch(showMessage(MESSAGES.DELETE_FAILURE, MessageType.Failure, false)),
  });

  const isDirty = useMemo(() => {
    if (!currentTableView) {
      return false;
    }

    return !isEqual(currentTableView?.attributes.configuration.filters, omitBy(isNil, filters));
  }, [currentTableView, filters]);

  return useMemo(
    () => ({
      loadingTableViews,
      loadTableViews,
      handleCreateTableView,
      handleDuplicateTableView,
      handleSaveTableView,
      handleSetCurrentTableView,
      handleArchiveTableView,
      tableViews,
      currentTableView,
      isDirty,
      canViewTableViews,
      canWriteTableViews,
    }),
    [
      loadingTableViews,
      loadTableViews,
      handleCreateTableView,
      handleDuplicateTableView,
      handleSaveTableView,
      handleSetCurrentTableView,
      handleArchiveTableView,
      tableViews,
      currentTableView,
      isDirty,
      canViewTableViews,
      canWriteTableViews,
    ],
  );
};

export default useTableViews;
